import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MoreHorizontal, Send, ArrowLeft, User, Plus, X, Mail } from 'lucide-react';
import NavigationModal from '../components/NavigationModal';
import NavigationCarousel from '../components/NavigationCarousel';
import { useTheme } from '../contexts/ThemeContext';
import DarkModeToggle from '../components/DarkModeToggle';
import { logout } from '../auth/config';

const Messages = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [conversationsList, setConversationsList] = useState([
    {
      id: 1,
      name: 'Dr. Sarah Al-Mahmoud',
      role: 'Architecture Professor',
      lastMessage: 'Great work on your spatial reasoning assignment!',
      time: '2m ago',
      unread: 2,
      avatar: 'S'
    },
    {
      id: 2,
      name: 'Study Group - Modern Architecture',
      role: 'Group Chat',
      lastMessage: 'Hassan: Who has the reading list for next week?',
      time: '15m ago',
      unread: 0,
      avatar: 'MA'
    },
    {
      id: 3,
      name: 'Ahmed Al-Rashid',
      role: 'Fellow Student',
      lastMessage: 'Thanks for sharing those AutoCAD files!',
      time: '1h ago',
      unread: 0,
      avatar: 'A'
    },
    {
      id: 4,
      name: 'AXORA Support',
      role: 'Customer Service',
      lastMessage: 'How can we help you today?',
      time: '1d ago',
      unread: 0,
      avatar: 'AX'
    }
  ]);
  const messageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Check for signup user first, then demo user as fallback
  // const signupUser = JSON.parse(localStorage.getItem('user') || 'null');
  // const demoUser = checkAuth();
  // const user = signupUser || demoUser;

  // Handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Handle navigation carousel clicks
  const handleNavigationClick = (item) => {
    if (item.path === '/login') {
      // Handle logout
      logout();
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('onboardingComplete');
      navigate('/login');
    } else {
      navigate(item.path);
    }
  };

  // Available users on the application
  const availableUsers = [
    { id: 1, username: 'ahmed_alrashid', name: 'Ahmed Al-Rashid', email: 'ahmed.rashid@student.axora.edu', role: 'Student', avatar: 'A' },
    { id: 2, username: 'sarah_chen', name: 'Dr. Sarah Chen', email: 'sarah.chen@axora.edu', role: 'Professor', avatar: 'S' },
    { id: 3, username: 'mohammed_hassan', name: 'Mohammed Hassan', email: 'mohammed.hassan@student.axora.edu', role: 'Student', avatar: 'M' },
    { id: 4, username: 'emily_rodriguez', name: 'Emily Rodriguez', email: 'emily.rodriguez@student.axora.edu', role: 'Student', avatar: 'E' },
    { id: 5, username: 'omar_khalil', name: 'Omar Khalil', email: 'omar.khalil@student.axora.edu', role: 'Student', avatar: 'O' },
    { id: 6, username: 'lisa_anderson', name: 'Lisa Anderson', email: 'lisa.anderson@student.axora.edu', role: 'Student', avatar: 'L' },
    { id: 7, username: 'david_kim', name: 'David Kim', email: 'david.kim@student.axora.edu', role: 'Student', avatar: 'D' },
    { id: 8, username: 'fatima_alzahra', name: 'Fatima Al-Zahra', email: 'fatima.alzahra@student.axora.edu', role: 'Student', avatar: 'F' },
    { id: 9, username: 'james_wilson', name: 'James Wilson', email: 'james.wilson@student.axora.edu', role: 'Student', avatar: 'J' },
    { id: 10, username: 'priya_patel', name: 'Priya Patel', email: 'priya.patel@student.axora.edu', role: 'Student', avatar: 'P' }
  ];

  // Filter conversations based on search term
  const filteredConversations = conversationsList.filter(conv => 
    conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter available users for new chat
  const filteredUsers = availableUsers.filter(user => 
    user.name.toLowerCase().includes(newChatSearch.toLowerCase()) ||
    user.username.toLowerCase().includes(newChatSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(newChatSearch.toLowerCase())
  );

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
  };

  const handleNewChat = () => {
    setShowNewChatModal(true);
    setNewChatSearch('');
  };

  const handleStartChat = (user) => {
    // Create new conversation
    const newConversation = {
      id: Date.now(),
      name: user.name,
      role: user.role,
      lastMessage: 'Chat started',
      time: 'now',
      unread: 0,
      avatar: user.avatar,
      email: user.email,
      username: user.username
    };
    
    // Add to conversations list
    setConversationsList(prev => [newConversation, ...prev]);
    setSelectedChat(newConversation);
    setShowNewChatModal(false);
    setNewChatSearch('');
  };

  const messages = useMemo(() => (
    selectedChat
      ? [
          { id: 1, sender: 'Dr. Sarah Al-Mahmoud', content: 'Great work on your spatial reasoning assignment!', time: '2:30 PM', isSelf: false },
          { id: 2, sender: 'You', content: 'Thank you Dr. Al-Mahmoud! I really enjoyed working on the 3D visualization part.', time: '2:45 PM', isSelf: true },
          { id: 3, sender: 'Dr. Sarah Al-Mahmoud', content: 'Your attention to detail in the structural elements was impressive. Keep it up!', time: '3:00 PM', isSelf: false }
        ]
      : []
  ), [selectedChat]);

  // Keyboard visibility detection for mobile
  useEffect(() => {
    const handleResize = () => {
      // On mobile, if the viewport height changes significantly, keyboard is likely visible
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const screenHeight = window.screen.height;
      const keyboardThreshold = screenHeight * 0.75; // 75% of screen height
      
      setIsKeyboardVisible(viewportHeight < keyboardThreshold);
    };

    // Listen for viewport changes (keyboard show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive or keyboard opens
  useEffect(() => {
    if (messagesContainerRef.current && selectedChat) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, selectedChat, isKeyboardVisible]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim() && selectedChat) {
      // Add message logic here
      setNewMessage('');
      
      // Scroll to bottom after sending
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }
  };

  const handleInputFocus = () => {
    // Scroll to bottom when input is focused (keyboard opens)
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Fixed Navigation Overlay */}
      <div className="fixed top-0 left-0 z-50" style={{ zIndex: 60 }}>
        <NavigationCarousel
          menuItems={[
            { label: 'HOME', path: '/app' },
            { label: 'STUDY', path: '/app/study' },
            { label: 'CHALLENGES', path: '/app/challenges' },
            { label: 'COMMUNITY', path: '/app/community' },
            { label: 'PIN UP', path: '/app/pinup' },
            { label: 'PROGRESS', path: '/app/progress' },
            { label: 'MESSAGES', path: '/app/messages' },
            { label: 'PROFILE', path: '/app/profile' },
            { label: 'LOG OUT', path: '/login', isLogout: true }
          ]}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Student Dashboard Header */}
      <div className="bg-[#AC5757] rounded-l-lg" style={{ 
        marginLeft: isSidebarCollapsed ? '90px' : `calc(14vw + 10px)`,
        transition: 'margin-left 0.3s ease'
      }}>
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex-1">
            <h1 className="font-oswald font-medium text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[65px]">
              MESSAGES
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <DarkModeToggle size="small" />
            
            {/* Profile */}
            <button
              onClick={() => {/* Navigate to profile */}}
              className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <User size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-6 py-8 ${isKeyboardVisible ? 'h-[calc(100vh-8rem)]' : 'h-[calc(100vh-12rem)]'} md:h-[calc(100vh-6rem)] flex transition-all duration-300`} style={{ 
        marginLeft: isSidebarCollapsed ? '90px' : '14vw',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Conversations List */}
        <div className={`${selectedChat ? 'hidden md:block' : 'block'} w-full md:w-1/3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col`}>
          {/* Search and New Chat Header */}
          <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex gap-2 mb-3">
              <div className="flex-1 relative">
                <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              <button
                onClick={handleNewChat}
                className="px-4 py-2 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A] transition-colors flex items-center gap-2"
              >
                <Plus size={16} />
                New Chat
              </button>
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => setSelectedChat(conv)}
                className={`p-4 border-b cursor-pointer transition-colors ${
                  selectedChat?.id === conv.id 
                    ? `${isDarkMode ? 'bg-[#AC5757]/10 border-l-4 border-l-[#AC5757]' : 'bg-[#AC5757]/5 border-l-4 border-l-[#AC5757]'}`
                    : `${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#AC5757] rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {conv.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{conv.name}</h3>
                      {conv.unread > 0 && (
                        <span className="bg-[#AC5757] text-white text-xs rounded-full px-2 py-1 ml-2">
                          {conv.unread}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{conv.role}</p>
                    <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{conv.lastMessage}</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{conv.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${selectedChat ? 'block' : 'hidden md:flex'} flex-1 flex-col`}>
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  {/* Mobile Back Button */}
                  <button 
                    onClick={() => setSelectedChat(null)}
                    className={`md:hidden p-2 rounded-lg mr-2 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <ArrowLeft size={20} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  </button>
                  
                  <div className="w-10 h-10 bg-[#AC5757] rounded-full flex items-center justify-center text-white font-semibold">
                    {selectedChat.avatar}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedChat.name}</h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{selectedChat.role}</p>
                  </div>
                </div>
                <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <MoreHorizontal size={20} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                </button>
              </div>

              {/* Messages */}
              <div 
                ref={messagesContainerRef}
                className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
              >
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.isSelf ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                        message.isSelf
                          ? 'bg-[#AC5757] text-white'
                          : `${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className={`text-xs mt-1 ${
                        message.isSelf ? 'text-white/70' : `${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
                      }`}>
                        {message.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t p-4`}>
                <form onSubmit={handleSendMessage} className="flex items-center gap-3">
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="Type your message..."
                    className={`flex-1 px-4 py-2 border rounded-full focus:ring-2 focus:ring-[#AC5757] focus:border-transparent transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="bg-[#AC5757] text-white p-2 rounded-full hover:bg-[#8A4A4A] transition-colors disabled:bg-gray-300"
                  >
                    <Send size={20} />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className={`hidden md:flex flex-1 items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="text-center">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <Search size={32} className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Select a conversation</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Choose from your existing conversations or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Start New Chat
              </h3>
              <button
                onClick={() => setShowNewChatModal(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  placeholder="Search by name, username, or email..."
                  value={newChatSearch}
                  onChange={(e) => setNewChatSearch(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleStartChat(user)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#AC5757] rounded-full flex items-center justify-center text-white font-semibold">
                        {user.avatar}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {user.name}
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          @{user.username}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail size={12} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.email}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${
                          user.role === 'Professor' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <User size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                  <p className="text-sm">Try searching by name, username, or email</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <NavigationModal isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
};

export default Messages;